import { Component, OnDestroy } from '@angular/core';
import { UserModel } from '../../../models/user-model';
import { AuthService } from '../../../services/auth/auth.service';
import { Router, NavigationEnd, Event as RouterEvent } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { UtilService } from '../../../services/util/util.service';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: false
})
export class HeaderComponent implements OnDestroy {
  user: any = {};
  brand = '';
  isAdminPage = false;
  private routerSubscription: Subscription | undefined;
  lastSelectedLanguage: string = 'en';
  isAdmin: boolean = false;
  internalImage: string = '';
  secondaryInternalImage: string = '';

  constructor(private authService: AuthService, private router: Router, private utilService: UtilService, private translate: TranslateService,
    private route: ActivatedRoute) {
    this.assignResolvedUser();
  }

  ngOnInit(): void {
    const notLoggedInBrand = this.authService.getIDP() == 'vwhub' ? 'VW' : 'AU';
    this.brand = notLoggedInBrand;
    const nonLoggedInLanguage = this.getDefaultLanguage();

    this.setLogoBasedOnBrand(notLoggedInBrand, nonLoggedInLanguage);

    // Subscribe to router events to detect navigation changes
    // and update the header accordingly
    this.utilService.showSpinner();
    this.routerSubscription = this.router.events.pipe(
      filter((event: RouterEvent): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      // Update header based on the URL
      const currentUrl = event.urlAfterRedirects; // Use urlAfterRedirects to handle redirects
      this.updateHeader(currentUrl);
      // Re-evaluate resolved user on each navigation
      this.assignResolvedUser();
    });

    // Optional: Set initial header based on the current route when the component loads
    // This handles the case where the header component might load *after* initial navigation
    this.updateHeader(this.router.url);

    this.authService.loginEvent.subscribe((isLoggedIn) => {
      if (isLoggedIn) {
        this.authService.getUser().then((user) => {
          this.user = user;
          this.brand = (this.user.brand === 'VW') ? 'VW Now' : 'Audi Now';
          this.initializeLanguageWithUser(this.user);
          const language = localStorage.getItem('selectedLanguage') || this.user.preferredLanguage || 'en';
          this.setLogoBasedOnBrand(this.user.brand, language);
        });
      } else {
        this.user = null;
      }
    })
    setTimeout(() => {
      this.utilService.hideSpinner();
    }, 500);
  }

  private setLogoBasedOnBrand(brand: any, language: any) {
    if (brand === 'VW' && language === 'fr') {
      this.internalImage = 'assets/images/VWnow_Data Classification Internal_Logo.svg';
      this.secondaryInternalImage = 'assets/images/VWnow_Data Classification Internal_Title-French.svg';
    } else if (brand === 'AU' && language === 'fr') {
      this.internalImage = 'assets/images/Audi_rings_png.png';
      this.secondaryInternalImage = 'assets/images/AoA_Title-French.svg';
    } else if (brand === 'AU') {
      this.internalImage = 'assets/images/Audi_rings_png.png';
      this.secondaryInternalImage = 'assets/images/AoA_Title-English.svg';
    } else {
      this.internalImage = 'assets/images/VWnow_Data Classification Internal_Logo.svg';
      this.secondaryInternalImage = 'assets/images/VWnow_Data Classification Internal_Title-English.svg';
    }

    
  }

  private assignResolvedUser(): void {
    const routeDataUser = this.findDeepestChild(this.route)?.snapshot.data['user'];
    if (routeDataUser) {
      this.user = routeDataUser;
      this.isAdmin = !!this.user.userRoles?.includes('ADMIN');
      this.brand = (this.user.brand === 'VW') ? 'VW Now' : 'Audi Now';
      this.initializeLanguageWithUser(this.user);
    } else if (!this.user || !this.user.userId) {
      // Fallback fetch if resolver hasn't populated yet
      this.authService.loginEvent.subscribe((isLoggedIn) => {
        if (isLoggedIn) {
          this.authService.getUser().then(user => {
            this.user = user;
            this.isAdmin = !!this.user.userRoles?.includes('ADMIN');
            this.brand = (this.user.brand === 'VW') ? 'VW Now' : 'Audi Now';
            this.initializeLanguageWithUser(this.user);
          });
        }
      });
    }

    console.log('Assigned user in header1 :', this.user);
  }

  private findDeepestChild(route: ActivatedRoute): ActivatedRoute | null {
    let current: ActivatedRoute | null = route;
    while (current?.firstChild) {
      current = current.firstChild;
    }
    return current;
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
  }

  //Read the current URL and check if it contains 'ude-admin' or 'bulk-update'
  updateHeader(url: string): void {
    if (url.includes('ude-admin') || url.includes('bulk-update')) {
      this.isAdminPage = true;
    } else {
      this.isAdminPage = false;
    }
  }

  initializeLanguageWithUser(user: any): void {
    const language = localStorage.getItem(window.btoa(user.userId + 'lastselectedLanguage'));
    language !== "null" ? language : user.preferredLanguage ? user.preferredLanguage : 'en';
    this.lastSelectedLanguage = (language && language !== "null") ? language : 'en';
    this.selectLanguage(this.lastSelectedLanguage);
  }

  selectLanguage(language: string) {
    localStorage.setItem(window.btoa(this.user?.userId + 'lastselectedLanguage'), language.toLocaleLowerCase());
    localStorage.setItem('selectedLanguage', language);
    this.lastSelectedLanguage = language;
    this.translate.use(language);
    this.authService.selectedLanguage$.next(language);
    //reload logos based on language
    this.setLogoBasedOnBrand(this.user?.brand || this.brand, language);
  }
  
  getDefaultLanguage(): string {
    if (localStorage.getItem('selectedLanguage')) {
      this.lastSelectedLanguage = localStorage.getItem('selectedLanguage')!;
    } else {
      this.lastSelectedLanguage = this.utilService.getCookie('language') ?? 'en';
    }
    return this.lastSelectedLanguage;
  }
}
